'use client';

import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import LinearProgress from '@mui/material/LinearProgress';
import { Reveal, useInView, Count } from './Reveal';
import { LogoMark } from './Logo';
import { tokens } from '../theme';

const Section = ({ id, dark = false, children, sx }) => (
  <Box
    component="section"
    id={id}
    sx={{
      py: { xs: 7, md: 12 },
      ...(dark && { background: tokens.darkBg, color: '#fff', overflow: 'hidden' }),
      ...sx,
    }}
  >
    <Container>{children}</Container>
  </Box>
);

const Eyebrow = ({ children, dark }) => (
  <Reveal>
    <Typography variant="overline" sx={{ color: dark ? tokens.accent : 'primary.main', mb: 1.5 }}>
      {children}
    </Typography>
  </Reveal>
);

const Lede = ({ children, dark }) => (
  <Reveal delay={140}>
    <Typography variant="body1" sx={{ maxWidth: '60ch', mt: 2, color: dark ? 'rgba(226,225,255,.72)' : 'text.secondary' }}>
      {children}
    </Typography>
  </Reveal>
);

/* ────────────────────────────── the problem ───────────────────────────── */

const TASKS = [
  ['Audience targeting', '4 platforms, by hand'],
  ['Campaign copy', '12 variants a week'],
  ['Creative preparation', '3 aspect ratios each'],
  ['Budget allocation', 'Reconciled in a sheet'],
  ['Platform setup', 'Re-entered per channel'],
  ['Lead follow-up', 'Chased from an inbox'],
];

export function Problem() {
  const [ref, inView] = useInView({ threshold: 0.2 });
  return (
    <Section id="problem" dark>
      <Eyebrow dark>The week you actually have</Eyebrow>
      <Reveal delay={80}>
        <Typography variant="h2" sx={{ color: '#fff', maxWidth: '22ch' }}>
          Still running your ad campaigns{' '}
          <Box
            component="em"
            sx={{
              fontStyle: 'normal', color: 'transparent',
              background: 'linear-gradient(100deg,#C4A4FF 10%,#6EA8FF 90%)',
              backgroundClip: 'text', WebkitBackgroundClip: 'text',
            }}
          >
            manually?
          </Box>
        </Typography>
      </Reveal>

      <Box
        ref={ref}
        sx={{
          display: 'grid', gap: 1.5, mt: 5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' },
        }}
      >
        {TASKS.map(([label, meta], i) => (
          <Box
            key={label}
            sx={{
              border: '1px solid rgba(168,120,255,.18)', borderRadius: '14px',
              p: 2, background: 'rgba(255,255,255,.035)',
              opacity: inView ? 1 : 0,
              transform: inView ? 'none' : 'translateY(16px)',
              transition: `opacity .6s ${tokens.ease} ${120 + i * 90}ms, transform .6s ${tokens.ease} ${120 + i * 90}ms`,
            }}
          >
            <Typography variant="h3" sx={{ color: '#fff', mb: 0.25 }}>{label}</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(226,225,255,.6)' }}>{meta}</Typography>
          </Box>
        ))}
      </Box>

      <Reveal delay={300}>
        <Typography
          sx={{ mt: 4, pl: 2, borderLeft: `2px solid ${tokens.accent}`, color: 'rgba(226,225,255,.78)', fontSize: '1.05rem' }}
        >
          Six disconnected tasks. Every campaign. Every week.
        </Typography>
      </Reveal>
    </Section>
  );
}

/* ─────────────────────────────── the loop ─────────────────────────────── */

const STEPS = [
  ['Connect', 'Your own ad accounts, health-checked before anything is promised.'],
  ['Brief', 'Start from your website. Adzilio drafts the audience, channels and budget.'],
  ['Plan', 'A full plan you can edit — and lock the parts you decide yourself.'],
  ['Creative', 'Copy and visuals composed from your context, formatted per channel.'],
  ['Launch', 'Submitted, in review, live. Rejections are shown, not hidden.'],
  ['Qualify', 'Leads scored, accepted by your team, and followed up from your mailbox.'],
];

export function Loop() {
  const [ref, inView] = useInView({ threshold: 0.15 });
  return (
    <Section id="loop">
      <Eyebrow>One continuous loop</Eyebrow>
      <Reveal delay={80}><Typography variant="h2">From a website address to qualified pipeline.</Typography></Reveal>
      <Lede>
        You give Adzilio a goal and the limits it must work inside. It handles the rest, and stops at
        every point where the decision should be yours.
      </Lede>

      <Box ref={ref} sx={{ position: 'relative', mt: 6 }}>
        {/* the rail only means anything while all six sit on one line */}
        <Box
          aria-hidden
          sx={{
            display: { xs: 'none', lg: 'block' },
            position: 'absolute', left: 0, right: 0, top: 7, height: 2, background: tokens.line,
            '&::after': {
              content: '""', position: 'absolute', left: 0, top: 0, height: 2,
              width: inView ? '100%' : 0, background: tokens.grad,
              transition: `width 1.8s ${tokens.ease}`,
            },
          }}
        />
        <Box
          component="ol"
          sx={{
            listStyle: 'none', m: 0, p: 0, display: 'grid', gap: { xs: 3, lg: 1.5 },
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)', lg: 'repeat(6,1fr)' },
          }}
        >
          {STEPS.map(([name, body], i) => (
            <Box
              component="li"
              key={name}
              sx={{
                position: 'relative',
                opacity: inView ? 1 : 0,
                transform: inView ? 'none' : 'translateY(14px)',
                transition: `opacity .55s ${tokens.ease} ${200 + i * 110}ms, transform .55s ${tokens.ease} ${200 + i * 110}ms`,
              }}
            >
              <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'background.paper',
                         border: `2px solid ${tokens.accent}`, position: 'relative', zIndex: 1 }} />
              <Typography variant="h3" sx={{ mt: 2, mb: 0.5 }}>{name}</Typography>
              <Typography variant="body2">{body}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Section>
  );
}

/* ─────────────────────────────── the gates ────────────────────────────── */

const GATES = [
  ['01', 'Spend commitment', 'You see the whole plan — channels, split, audience, forecast — before a penny moves.', 'Blocks money'],
  ['02', 'Creative go-live', 'Every ad is approved by a human. This gate never auto-approves, not even on full autopilot.', 'Blocks publication'],
  ['03', 'Outreach send', 'Follow-ups go out from your own mailbox, to people who asked to hear from you.', 'Blocks contact'],
];

export function Gates() {
  const [ref, inView] = useInView({ threshold: 0.3 });
  return (
    <Section id="gates">
      <Eyebrow>Three approval gates</Eyebrow>
      <Reveal delay={80}>
        <Typography variant="h2">Automation runs the work. You keep the decisions that can&apos;t be undone.</Typography>
      </Reveal>
      <Lede>
        Adzilio acts on its own inside limits you set. Three actions are irreversible in public — so
        all three stop and wait for a person, at every automation level.
      </Lede>

      <Box ref={ref} sx={{ position: 'relative', mt: 6 }}>
        <Box aria-hidden sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute',
                               left: 0, right: 0, top: 27, height: 2, background: tokens.line }} />
        <Box aria-hidden sx={{
          display: { xs: 'none', md: 'block' }, position: 'absolute', left: 0, top: 27, height: 2,
          width: inView ? '100%' : 0, background: tokens.grad, transition: `width 1.9s ${tokens.ease}`,
        }} />
        <Box sx={{ position: 'relative', display: 'grid', gap: { xs: 4, md: 3 },
                   gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' } }}>
          {GATES.map(([n, title, body, blocks], i) => (
            <Box component="article" key={n}>
              <Box sx={{
                width: 56, height: 56, borderRadius: '16px', bgcolor: 'background.paper',
                border: '1px solid', borderColor: inView ? tokens.accent : tokens.line,
                display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: '1.2rem',
                letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums',
                color: inView ? 'primary.main' : 'text.disabled',
                boxShadow: inView ? '0 10px 26px -10px rgba(91,55,238,.5)' : tokens.shadow,
                position: 'relative', zIndex: 1,
                transition: `color .45s ${tokens.ease} ${350 + i * 380}ms, border-color .45s ${tokens.ease} ${350 + i * 380}ms, box-shadow .45s ${tokens.ease} ${350 + i * 380}ms`,
              }}>{n}</Box>
              <Typography variant="h3" sx={{ mt: 2.5 }}>{title}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5, maxWidth: '32ch' }}>{body}</Typography>
              <Chip label={blocks} size="small"
                    sx={{ mt: 1.5, borderRadius: '7px', bgcolor: 'rgba(91,55,238,.08)',
                          color: 'primary.main', fontWeight: 600, fontSize: '.78rem' }} />
            </Box>
          ))}
        </Box>
      </Box>
    </Section>
  );
}

/* ───────────────────────────── honest reporting ───────────────────────── */

const HONEST = [
  ['Forecasts', 'A range, not a promise', 'Every projection is given as a range, with the reasoning behind each choice in plain language.'],
  ['Reporting', 'Drop-off is always shown', 'Captured, qualified, accepted, opportunity, won — with the fall between each stage in view.'],
  ['Optimization', 'Cost per qualified lead', 'Budget moves toward leads your team actually accepted, never toward whatever is cheapest to click.'],
  ['Autonomy', 'Logged and reversible', 'Every automated action is written down in plain English, with the reason and a one-click undo.'],
  ['Control', 'Lock what you decide', 'Lock a budget, an audience or a headline and re-optimization will never touch it again.'],
  ['Launch', 'Honest states', 'Submitted, in review, live. When a platform rejects an ad, you see that too — and the fix.'],
];

export function Honest() {
  return (
    <Section id="honest">
      <Eyebrow>Built to be checked</Eyebrow>
      <Reveal delay={80}><Typography variant="h2">The numbers don&apos;t flatter us.</Typography></Reveal>
      <Lede>Most ad tools show you the metric that looks best. Adzilio is built the other way round.</Lede>

      <Box sx={{ display: 'grid', gap: 2, mt: 5,
                 gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' } }}>
        {HONEST.map(([tag, title, body], i) => (
          <Reveal key={title} delay={120 + i * 70}>
            <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
              <Typography variant="overline" sx={{ color: 'primary.main', mb: 1.5 }}>{tag}</Typography>
              <Typography variant="h3">{title}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{body}</Typography>
            </Paper>
          </Reveal>
        ))}
      </Box>
    </Section>
  );
}

/* ─────────────────────────────── the funnel ───────────────────────────── */

const FUNNEL = [
  ['Captured', 214, 100, ''],
  ['Fit — scored by AI', 138, 64, '64%'],
  ['Accepted by your team', 92, 43, '67%'],
  ['Opportunity', 34, 16, '37%'],
  ['Won', 9, 4, '26%'],
];

export function Funnel() {
  const [ref, inView] = useInView({ threshold: 0.3 });
  return (
    <Section id="funnel">
      <Eyebrow>One campaign, honestly reported</Eyebrow>
      <Reveal delay={80}><Typography variant="h2">Every stage, including the ones that hurt.</Typography></Reveal>
      <Lede>
        A lead count on its own tells you nothing. Adzilio optimizes toward the leads your team
        accepted — and shows you what fell away at each step.
      </Lede>

      <Stack ref={ref} spacing={1.5} sx={{ mt: 5 }}>
        {FUNNEL.map(([label, value, pct, drop], i) => (
          <Box key={label} sx={{ display: 'grid', alignItems: 'center', gap: 2,
                                 gridTemplateColumns: { xs: '1fr auto 48px', sm: 'minmax(150px,1fr) 3fr auto 52px' } }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', gridColumn: { xs: '1', sm: 'auto' } }}>
              {label}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={inView ? pct : 0}
              sx={{
                height: 34, borderRadius: '9px', bgcolor: tokens.soft,
                border: `1px solid ${tokens.line}`,
                gridColumn: { xs: '1 / -1', sm: 'auto' },
                order: { xs: 3, sm: 0 },
                '& .MuiLinearProgress-bar': {
                  background: tokens.grad, borderRadius: '8px',
                  transition: `transform 1.2s ${tokens.ease} ${i * 130}ms`,
                },
              }}
            />
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
              <Count to={value} />
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {drop}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Section>
  );
}

/* ───────────────────────────────  channels  ───────────────────────────── */

const CHANNELS = ['Facebook', 'Instagram', 'Threads', 'LinkedIn', 'Google', 'YouTube', 'TikTok', 'Pinterest', 'Reddit', 'X'];

export function Channels() {
  return (
    <Section id="channels">
      <Eyebrow>One brief, ten channels</Eyebrow>
      <Reveal delay={80}><Typography variant="h2">Connect the accounts you already own.</Typography></Reveal>
      <Lede>
        Campaigns run on your ad accounts and are paid with your card. Adzilio publishes only to the
        channels you connect and approve — and never touches your ad budget.
      </Lede>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 4 }}>
        {CHANNELS.map((c, i) => (
          <Reveal key={c} delay={100 + i * 45}>
            <Chip label={c} variant="outlined" sx={{ height: 38, px: 0.5, fontSize: '.92rem' }} />
          </Reveal>
        ))}
      </Box>
    </Section>
  );
}

/* ─────────────────────────────── early access ─────────────────────────── */

const CONTACT_EMAIL = 'info@adzilio.com';

/* No input field: the button hands off to the visitor's own mail client, which
   already knows their address — asking them to type it in first would be asking
   for it twice. */
const MAILTO =
  `mailto:${CONTACT_EMAIL}` +
  `?subject=${encodeURIComponent('Adzilio early access')}` +
  `&body=${encodeURIComponent("I'd like to be on the early-access list.")}`;

export function EarlyAccess() {
  const cardRef = React.useRef(null);

  /* The film's closing CTA scrolls here and then fires this, so the card plays
     its arrival instead of just appearing. Restarting a CSS animation needs the
     class removed, a reflow read, then the class back — without the reflow the
     browser coalesces both changes and nothing replays. */
  React.useEffect(() => {
    const play = () => {
      const el = cardRef.current;
      if (!el) return;
      el.classList.remove('ea-arrive');
      void el.offsetWidth;
      el.classList.add('ea-arrive');
    };
    window.addEventListener('adzilio:early', play);
    return () => window.removeEventListener('adzilio:early', play);
  }, []);

  return (
    <Section id="early">
      <Reveal>
        {/* a translucent surface, not another gradient — the page gradient
            shows through, so the card reads as raised rather than as a
            misaligned second copy of the background */}
        <Box ref={cardRef} sx={{
          background: 'rgba(255,255,255,.035)', borderRadius: '22px', color: '#fff',
          p: { xs: 4, md: 7 }, border: `1px solid ${tokens.lineOn}`, boxShadow: tokens.shadow,
        }}>
          {/* the card runs the full container width, so the copy sits left and
              the action gets its own column rather than leaving half of it empty */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.1fr .9fr' },
            alignItems: 'center',
          }}>
            <Box sx={{ pr: { md: 7 }, pb: { xs: 4, md: 0 } }}>
              <Typography data-ea="1" variant="overline" sx={{ color: tokens.accent, mb: 1.5 }}>Early access</Typography>
              <Typography data-ea="2" variant="h2" sx={{ color: '#fff' }}>Be first to hand over the busywork.</Typography>
              <Typography data-ea="3" sx={{ color: 'rgba(237,231,255,.72)', maxWidth: '46ch', mt: 1.5 }}>
                Adzilio is in development. Write to us and we&apos;ll be in touch the moment
                early access opens.
              </Typography>
            </Box>

            <Box sx={{
              pl: { md: 7 }, pt: { xs: 4, md: 0 },
              borderLeft: { md: `1px solid ${tokens.lineOn}` },
              borderTop: { xs: `1px solid ${tokens.lineOn}`, md: 'none' },
            }}>
              <Button
                data-ea="4"
                href={MAILTO}
                variant="contained"
                fullWidth
                sx={{ height: 56, fontSize: '1rem' }}
              >
                Request early access
              </Button>
              {/* mailto: does nothing at all on a machine with no mail client
                  configured, so the address is spelled out as the way through */}
              <Typography data-ea="5" variant="body2" sx={{ mt: 2, color: 'rgba(237,231,255,.6)' }}>
                Opens your email app. Or write to{' '}
                <Link href={MAILTO} sx={{ color: tokens.accent, fontWeight: 500 }}>
                  {CONTACT_EMAIL}
                </Link>
                .
              </Typography>
            </Box>
          </Box>
        </Box>
      </Reveal>
    </Section>
  );
}

/* ─────────────────────────────────  footer  ───────────────────────────── */

export function Footer() {
  return (
    <Box component="footer" sx={{ borderTop: `1px solid ${tokens.lineOn}`, py: 5 }}>
      <Container>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <LogoMark size={22} />
            <Typography variant="body2">© 2026 Adzilio</Typography>
          </Stack>
          <Typography variant="body2">AI does the heavy lifting. You stay in control.</Typography>
        </Stack>
      </Container>
    </Box>
  );
}
